/**
 * Renderer struk ESC/POS — **fungsi murni**: `(struk, profil) → Uint8Array`.
 *
 * ### ⭐ Kenapa dipisah dari transport
 * Byte-nya identik apakah dikirim lewat Ethernet, USB, atau Bluetooth. Dengan memisahkannya,
 * **90% jalur printer bisa diuji tanpa satu perangkat pun** — golden file, byte-per-byte. Yang
 * tersisa untuk perangkat nyata hanya lapisan transport yang tipis.
 *
 * ⛔ **Yang TIDAK bisa dibuktikan uji ini, dan itu disebut apa adanya:** apakah model printer
 * tertentu benar-benar mencetaknya. Codepage huruf Indonesia, potong kertas, tendangan laci, dan
 * lebar 58 vs 80 mm hanya terbukti dari kertas yang keluar. Mock membawa kita ~90%; sisanya persis
 * "spike" yang tak bisa diselesaikan dengan berpikir.
 *
 * ### Uang di struk
 * Nilai datang sebagai **string desimal kanonik** dari kontrak dan hanya diformat untuk
 * ditampilkan. ⚠️ Renderer **tak pernah menghitung** — struk yang menghitung ulang totalnya sendiri
 * adalah struk yang bisa berbeda dari yang tersimpan.
 *
 * @module
 */
import { COLUMNS, COLUMNS_BY_FONT, DOTS, decodeLogoRaster } from '@osbpos/contract';
/* ── perintah ESC/POS ────────────────────────────────────────────────────── */
const ESC = 0x1b;
const GS = 0x1d;
/**
 * ⭐⭐ **Kuantitas untuk DICETAK** — `"1.000000"` jadi `"1"`, `"0.500000"` jadi `"0,5"`.
 *
 * ⛔ Sengaja BUKAN `Math.round`. Di F&B kuantitas hampir selalu bulat, tapi "hampir" bukan
 * "selalu": seafood ditimbang per 100 gram, prasmanan per kilo, dan bahan resep (K30) memang
 * pecahan. Membulatkan berarti struk 0,5 kg tertulis **1 kg** — angka yang salah, dicetak, dan
 * diserahkan ke tamu.
 *
 * ⚠️ Yang dibuang cuma nol di belakang koma. Ketelitiannya tetap utuh di DB (Guardrail #1);
 * yang diperbaiki bacaannya.
 *
 * ⭐ Koma sebagai pemisah desimal — sejalan dengan cara angka ditulis di Indonesia, dan titik di
 * sini sudah dipakai sebagai pemisah ribuan oleh `formatRupiah` di baris yang sama.
 */
export function formatQty(kanonik) {
    const [utuh = '0', pecahan = ''] = String(kanonik).split('.');
    const sisa = pecahan.replace(/0+$/, '');
    return sisa === '' ? utuh : `${utuh},${sisa}`;
}
/** Ribuan bertitik dari string desimal kanonik. Hanya TAMPILAN — tak pernah dipakai berhitung. */
export function formatRupiah(kanonik) {
    const [utuh = '0'] = kanonik.split('.');
    const negatif = utuh.startsWith('-');
    const digits = (negatif ? utuh.slice(1) : utuh).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negatif ? '-' : ''}${digits}`;
}
/** Potong string agar muat kolom; ⚠️ memotong, tak pernah membungkus diam-diam ke baris berikutnya. */
/**
 * ⭐⭐ **Alih-aksara ke ASCII** sebelum byte dibentuk — ketahuan dari MELIHAT cetakan `.txt`
 * (Imron, 2026-08-04): tanda pisah `—` di catatan kaki tenant keluar sebagai **`?`**.
 *
 * ### ⛔ Kenapa mengganti dengan `?` itu salah, bukan sekadar jelek
 * Teks kepala & kaki struk **diketik tenant** di portal — dan orang mengetik tanda pisah, kutip
 * melengkung, dan elipsis tanpa menyadarinya (penyunting teks mengubahnya sendiri). Struk yang
 * memuat `Terima kasih ? simpan struk ini` terbaca seperti sistem yang rusak, di tempat yang paling
 * dilihat tamu.
 *
 * ⚠️ Dan `?` menghapus makna: `—` masih terbaca sebagai `-`, tapi `?` tidak terbaca sebagai apa pun.
 *
 * ⛔ Codepage PC437 juga TAK memuat huruf beraksen di 0x80–0xFF sebagaimana Latin-1 — melewatkan
 * byte mentah ke sana menghasilkan glif acak. Karena itu batasnya `0x7e`, bukan `0xff`.
 */
const ALIH = {
    '—': '-', '–': '-', '−': '-',
    '\u2018': "'", '\u2019': "'", '\u201c': '"', '\u201d': '"',
    '…': '...', '\u00a0': ' ', '•': '*', '×': 'x', '·': '.',
    /* Huruf beraksen yang lazim di nama tenant & alamat Indonesia. */
    'é': 'e', 'è': 'e', 'ë': 'e', 'á': 'a', 'à': 'a', 'ä': 'a', 'í': 'i', 'ó': 'o', 'ö': 'o',
    'ú': 'u', 'ü': 'u', 'ñ': 'n', 'ç': 'c',
    'É': 'E', 'Á': 'A', 'Í': 'I', 'Ó': 'O', 'Ú': 'U', 'Ñ': 'N', 'Ç': 'C',
};
export function toAscii(s) {
    let out = '';
    for (const ch of s)
        out += ALIH[ch] ?? ch;
    return out;
}
/**
 * ⭐⭐ Memotong dengan **penanda `..`** (Imron, 2026-08-04) — bukan diam-diam.
 *
 * ### ⛔ Kenapa penanda, padahal ia memakan dua karakter yang sudah sempit
 * Pemotongan tanpa penanda **berbohong**: "Nasi Goreng Kampung Spesial Telur Mata Sapi" tercetak
 * sebagai "Nasi Goreng Kampung Spesial Telu" yang terbaca sebagai nama yang lengkap — dan dua
 * produk berbeda yang 32 karakter pertamanya sama menjadi **tak terbedakan di struk**. Begitu pula
 * "Service Charge 5%" yang jadi "Service Charge 5", terbaca sebagai angka, bukan tarif.
 *
 * ⚠️ Panjangnya tetap tepat `n` — dua karakter penanda mengambil tempat isi, bukan menambah lebar.
 * Kalau tidak, satu nama panjang akan mendorong kolom rupiah keluar kertas dan merusak seluruh
 * baris di bawahnya.
 */
function truncate(s, n) {
    if (s.length <= n)
        return s;
    /* ⚠️ Terlalu sempit untuk penanda ⇒ potong apa adanya; `..` yang memakan seluruh ruang tak
       menyampaikan apa pun selain bahwa ada sesuatu yang hilang. */
    if (n <= 2)
        return s.slice(0, n);
    return `${s.slice(0, n - 2)}..`;
}
/** Baris dua kolom: kiri rata-kiri, kanan rata-kanan, dipisah spasi. */
function twoColumns(left, kanan, lebar) {
    const k = truncate(left, Math.max(0, lebar - kanan.length - 1));
    return `${k}${' '.repeat(Math.max(1, lebar - k.length - kanan.length))}${kanan}`;
}
function tengah(s, lebar) {
    const t = truncate(s, lebar);
    const left = Math.floor((lebar - t.length) / 2);
    return `${' '.repeat(Math.max(0, left))}${t}`;
}
class Buf {
    parts = [];
    /** ⭐ Untuk muatan BESAR (raster logo) — tanpa spread, supaya tak melampaui batas argumen. */
    bytesRaw(b) {
        for (const x of b)
            this.parts.push(x);
        return this;
    }
    raw(...b) {
        this.parts.push(...b);
        return this;
    }
    /**
     * Teks + newline. ⚠️ Disandikan **latin1**, bukan UTF-8: printer termal ESC/POS memakai
     * codepage 1 byte. Karakter di luar codepage jadi `?` — terlihat, bukan diam-diam hilang.
     */
    text(s) {
        for (const ch of `${toAscii(s)}\n`) {
            const c = ch.codePointAt(0) ?? 63;
            this.parts.push(c > 0x7e ? 63 : c);
        }
        return this;
    }
    /**
     * Perataan teks (`ESC a n`). ⚠️ Dikembalikan ke kiri secara eksplisit oleh pemanggil — printer
     * MENGINGAT perataan terakhir, dan struk yang seluruhnya rata tengah terbaca seperti pengumuman.
     */
    align(mode) {
        return this.raw(0x1b, 0x61, mode === 'center' ? 0x01 : 0x00);
    }
    bytes() {
        return Uint8Array.from(this.parts);
    }
}
/**
 * Merender struk penjualan.
 *
 * Urutan perintah dipilih supaya **aman bila printer mengabaikan sebagiannya**: init dulu, codepage
 * berikutnya, lalu isi; potong & laci paling akhir. Printer yang tak punya pemotong akan
 * mengabaikan `GS V` tanpa merusak apa pun sebelumnya.
 */
/**
 * Jenis dokumen cetak.
 *
 * ⭐ **`bill` (sebelum bayar) ≠ `receipt` (sesudah bayar)** — dan bedanya bukan kosmetik:
 * | | `bill` | `receipt` |
 * |---|---|---|
 * | Baris tender & kembalian | **tidak ada** (belum dibayar) | ada |
 * | Kepala | "TAGIHAN — BELUM DIBAYAR" | nama outlet saja |
 * | Laci kas | ⛔ **TIDAK PERNAH terbuka** | terbuka bila metode bayarnya menuntut |
 *
 * ⛔ Baris terakhir itu kontrol, bukan preferensi: laci yang terbuka saat mencetak tagihan berarti
 * laci bisa dibuka **tanpa transaksi apa pun** — cukup tekan "cetak bill" berulang kali.
 */
/**
 * ⭐⭐⭐ **Perintah tendang laci** — `ESC p 0 25 250` (pulsa 25 ms, jeda 250 ms, konektor 0).
 *
 * Satu-satunya tempat urutan byte ini hidup. Dipakai renderer struk DAN layar kasir; menyalinnya
 * ke salah satunya berarti dua salinan yang bisa menyimpang — dan yang menyimpang di sini adalah
 * lamanya solenoid dialiri arus.
 */
const DRAWER_KICK = [ESC, 0x70, 0x00, 0x19, 0xfa];
/**
 * ⭐⭐⭐ **Byte tendang laci untuk dikirim SENDIRI oleh layar kasir** (Imron, 2026-08-25:
 * *"tendang laci tidak perlu lewat server. waktu pilih tender bertipe tendang laci, kasir harusnya
 * sudah tahu"*).
 *
 * ### ⛔ Kenapa K27 tetap utuh
 * K27 melarang klien **merakit struk**, dan alasannya tetap berlaku: struk memuat angka uang yang
 * harus identik dengan yang tercatat. Yang di sini bukan struk — ia perintah PERANGKAT tanpa satu
 * pun angka, dan bytenya tetap datang dari paket renderer bersama, bukan dikarang di shell.
 *
 * ### ⭐ Kenapa kasir memang sudah tahu
 * `payment_method.opens_drawer` sudah ikut turun di katalog sejak lama. Menunggu server
 * mengembalikan byte hanya untuk memberi tahu klien apa yang sudah ada di tangannya berarti laci
 * tak bisa dibuka saat jaringan mati — tepat ketika kasir paling butuh memberi kembalian.
 *
 * ⚠️ Mengembalikan array KOSONG bila printernya memang tak punya laci: laci bukan sifat printer
 * yang bisa diasumsikan, dan mengirim pulsa ke printer tanpa konektor laci tak menghasilkan apa-apa
 * selain byte yang membingungkan saat ditelusuri.
 */
export function drawerKickBytes(p) {
    return p.hasDrawerKick ? Uint8Array.from(DRAWER_KICK) : new Uint8Array(0);
}
export function renderReceipt(r, p, docType = 'receipt', 
/**
 * ⭐⭐ **Tendangan laci bisa DIMATIKAN** — dipakai saat layar kasir sudah menendangnya sendiri
 * di titik tender (lihat {@link drawerKickBytes}). ⛔ Tanpa ini laci tertendang DUA KALI: sekali
 * saat uang diterima, sekali lagi saat struknya keluar — dan di tier tablet jeda keduanya bisa
 * semenit, jadi laci yang sudah ditutup **membuka sendiri** tanpa ada yang menyentuhnya.
 *
 * ⚠️ Bawaannya tetap `true`: jalur yang tak melewati layar kasir (outlet-server, cetak dari
 * portal) tak punya siapa pun yang sudah menendang.
 */
opts = {}) {
    /*
     * ⭐⭐⭐ Kolom mengikuti FONT, bukan cuma lebar kertas. Ini yang membuat `font: 'small'` benar-
     * benar bermanfaat (lebih banyak isi per baris) alih-alih sekadar huruf kecil dengan kolom yang
     * salah tempat.
     */
    const font = r.template?.font ?? 'normal';
    const w = COLUMNS_BY_FONT[font][p.paperWidth];
    const b = new Buf();
    b.raw(ESC, 0x40); // ESC @ — inisialisasi
    b.raw(ESC, 0x74, p.codepage); // ESC t n — codepage
    /* `ESC M n` — 0 = Font A (12×24), 1 = Font B (9×17). ⚠️ Sesudah `ESC @`, kalau tidak ia direset. */
    if (font === 'small')
        b.raw(ESC, 0x4d, 0x01);
    /*
     * ⭐⭐ **Logo di paling atas, rata tengah** — sebelum nama outlet.
     *
     * `GS v 0 m xL xH yL yH` — `xL/xH` menghitung **BYTE per baris**, `yL/yH` menghitung **BARIS**.
     * ⛔ Menukar keduanya menghasilkan gambar yang "hampir benar": ia bergeser makin jauh tiap baris,
     * dan yang melihat hasilnya cuma tamu.
     *
     * ⚠️ Raster yang lebih lebar dari kertas DILEWATI, bukan dipotong: separuh logo yang tercetak
     * terbaca sebagai printer rusak, sementara tak ada logo terbaca sebagai belum disetel — dan yang
     * kedua itulah yang sebenarnya terjadi.
     */
    const logo = decodeLogoRaster(r.template?.logoRaster);
    if (logo !== undefined && logo.width <= DOTS[p.paperWidth]) {
        const perRow = Math.ceil(logo.width / 8);
        b.raw(ESC, 0x61, 0x01); // rata tengah
        b.raw(GS, 0x76, 0x30, 0x00, perRow & 0xff, (perRow >> 8) & 0xff, logo.height & 0xff, (logo.height >> 8) & 0xff);
        /* ⚠️ Lewat array, bukan spread langsung: raster 576×120 itu ~8.600 byte, dan spread
           sebanyak itu ke argumen fungsi bisa melampaui batas tumpukan pemanggilan. */
        b.bytesRaw(logo.bits);
        b.raw(ESC, 0x61, 0x00);
    }
    /*
     * ⚠️ `GS ! 0x11` = dobel lebar + dobel tinggi. Yang dobel lebar memakan DUA kolom per huruf, jadi
     * pemenggalannya memakai `w / 2` — tanpa itu nama panjang meluber ke baris kedua dan kepala
     * struknya rusak justru pada outlet yang namanya paling panjang.
     */
    const besar = (r.template?.nameScale ?? 'normal') === 'large';
    b.raw(ESC, 0x61, 0x01); // rata tengah
    b.raw(ESC, 0x45, 0x01); // tebal on
    if (besar)
        b.raw(GS, 0x21, 0x11);
    b.text(truncate(r.outletName, besar ? Math.floor(w / 2) : w));
    if (besar)
        b.raw(GS, 0x21, 0x00);
    b.raw(ESC, 0x45, 0x00); // tebal off
    b.raw(ESC, 0x61, 0x00); // rata kiri
    /*
     * ⭐ Alamat langsung di bawah nama outlet, rata tengah — bentuk struk Indonesia yang lazim.
     * ⛔ Baris kosong TIDAK dicetak: kertas termal mahal, dan struk yang panjang tanpa isi membuat
     * orang berhenti membacanya.
     */
    const address = [r.template?.address1, r.template?.address2]
        .filter((x) => x !== undefined && x.trim() !== '');
    if (address.length > 0) {
        b.align('center');
        for (const line of address)
            b.text(truncate(line.trim(), w));
        // ⚠️ Dikembalikan ke kiri HANYA bila sempat dipindah — printer mengingat perataan terakhir,
        // dan byte yang tak perlu tetap byte yang harus dijelaskan saat golden test berubah.
        b.align('left');
    }
    /*
     * ⛔⛔ **Nomor pesanan TAK BOLEH terpenggal** — dan ini ketahuan dari MELIHAT cetakan `.txt`,
     * bukan dari uji byte (Imron, 2026-08-04).
     *
     * Di kertas 58mm, `twoColumns` memberi nomor pesanan sisa lebar setelah jam: 32 − 16 − 1 = 15
     * karakter. `SMB/2026-08-04/000137` terpotong jadi `SMB/2026-08-04/` — **justru bagian yang
     * membedakannya** yang hilang. Nomor pesanan adalah satu-satunya tali antara kertas di tangan
     * tamu dan baris di basis data; nomor yang tak membedakan apa pun sama saja tak dicetak.
     *
     * ⇒ Kalau tak muat berdampingan, ia mendapat barisnya sendiri. Satu baris kertas jauh lebih
     * murah daripada struk yang tak bisa dijodohkan saat tamu komplain.
     */
    if (r.orderNo.length + r.printedAt.length + 1 <= w) {
        b.text(twoColumns(r.orderNo, r.printedAt, w));
    }
    else {
        b.text(truncate(r.orderNo, w));
        b.text(truncate(r.printedAt, w));
    }
    /* ⛔ Sama: biarkan lebar kertas yang menentukan, bukan angka yang dipahat. */
    b.text(twoColumns(`Kasir: ${r.cashier}`, r.businessDate, w));
    /*
     * ⭐⭐ Penanda SALINAN berdiri di ATAS rincian, bukan di kaki: yang membaca struk berhenti
     * begitu angkanya ketemu, dan penanda yang baru muncul sesudah total adalah penanda yang tak
     * dibaca. Ia sengaja rata tengah & berdiri sendiri — sama seperti "TAGIHAN — BELUM DIBAYAR".
     */
    if (opts.copy === true) {
        b.align('center').text(truncate('** SALINAN — CETAK ULANG **', w)).align('left');
    }
    b.text('-'.repeat(w));
    for (const l of r.lines) {
        if (l.isChild === true) {
            /*
             * ⭐⭐ **Kebijakan `printZeroPriceModifiers` akhirnya DIPAKAI** (0046).
             *
             * ⛔ Kolomnya ada sejak Fase 0 dan tak pernah dibaca satu baris kode pun — pemilik yang
             * mematikannya melihat setelannya tersimpan dan menyimpulkan tiketnya akan memendek,
             * padahal tak ada yang berubah. Setelan yang tak dibaca BERBOHONG ke pemiliknya.
             *
             * ⚠️ Yang disembunyikan HANYA modifier gratis ("Tanpa es", "Pedas sedang"). Modifier
             * BERBAYAR selalu tercetak, apa pun kebijakannya — ia mengubah yang dibayar tamu, dan
             * struk yang menyembunyikan sebab kenaikan adalah struk yang akan dibantah.
             */
            const gratis = l.priceDelta === undefined || l.priceDelta === '0.0000';
            if (gratis && r.printZeroPriceModifiers === false)
                continue;
            /*
             * ⭐ Anak menjorok dua spasi + penanda. Yang BERBAYAR menampilkan selisihnya di kanan —
             * tamu berhak melihat kenapa totalnya bertambah, dan menyembunyikannya adalah cara paling
             * halus membuat struk dibantah.
             */
            const priced = l.priceDelta !== undefined && l.priceDelta !== '0.0000';
            b.text(priced
                ? twoColumns(`  + ${truncate(l.name, w - 14)}`, `+${formatRupiah(l.priceDelta)}`, w)
                : truncate(`  - ${l.name}`, w));
            continue;
        }
        b.text(truncate(l.name, w));
        b.text(twoColumns(`  ${l.qty}`, formatRupiah(l.amount), w));
    }
    b.text('-'.repeat(w));
    b.text(twoColumns('Subtotal', formatRupiah(r.subtotal), w));
    if (r.discount !== '0.0000')
        b.text(twoColumns('Diskon', `-${formatRupiah(r.discount)}`, w));
    // ⭐ Pajak = BARIS (K11). Menambah jenis pajak TIDAK mengubah renderer ini.
    /*
     * ⛔ Batas 16 karakter yang dulu dipahat di sini DIBUANG (Imron, 2026-08-04). `twoColumns` sudah
     * memotong menurut lebar kertas yang sesungguhnya — batas kedua hanya membuang kolom yang
     * tersedia: di 58mm ia memangkas 9 karakter yang muat, di 80mm hampir 20. Akibatnya
     * "Service Charge 5%" tercetak "Service Charge.." padahal ruangnya cukup, dan tarifnya hilang
     * dari struk tanpa alasan.
     *
     * ⚠️ Angka ajaib seperti ini menua diam-diam: ia benar sekali waktu untuk satu lebar kertas, lalu
     * salah selamanya untuk semua yang lain.
     */
    for (const t of r.taxes)
        b.text(twoColumns(t.name, formatRupiah(t.amount), w));
    b.raw(ESC, 0x45, 0x01);
    b.text(twoColumns('TOTAL', formatRupiah(r.grandTotal), w));
    b.raw(ESC, 0x45, 0x00);
    /*
     * ⛔ Tagihan TIDAK memuat baris tender: belum ada yang dibayar, dan mencetak baris pembayaran
     * pada dokumen yang belum dibayar adalah cara paling langsung membuat tamu mengira sudah lunas.
     */
    if (docType === 'receipt') {
        for (const t of r.tenders)
            b.text(twoColumns(truncate(t.name, 16), formatRupiah(t.amount), w));
        if (r.change !== undefined)
            b.text(twoColumns('Kembali', formatRupiah(r.change), w));
    }
    else {
        b.text('');
        b.align('center').text('** TAGIHAN — BELUM DIBAYAR **').align('left');
    }
    /*
     * ⚠️ SATU kaki struk, bukan dua. `template.footnote` datang dari portal (`outlet_settings`),
     * `footer` adalah pengesampingan eksplisit per-cetak. Dua mekanisme kaki yang mirip akan
     * berakhir dengan salah satunya diisi dan yang lain terlupa — lalu tak seorang pun tahu mana
     * yang sedang tercetak.
     */
    /*
     * ⭐ Empat baris kaki (2026-08-25). ⛔ `footer` per-cetak tetap MENGGANTIKAN seluruhnya, bukan
     * menumpuk: dua mekanisme kaki yang saling menambah akan berakhir dengan struk yang kakinya
     * berbeda menurut jalur pemanggilan, dan tak seorang pun tahu mana yang sedang tercetak.
     */
    const kaki = r.footer !== undefined
        ? [r.footer]
        : [r.template?.footnote, r.template?.footnote2, r.template?.footnote3, r.template?.footnote4];
    const kakiIsi = kaki.filter((x) => x !== undefined && x.trim() !== '');
    if (kakiIsi.length > 0) {
        b.text('');
        b.raw(ESC, 0x61, 0x01);
        for (const baris of kakiIsi)
            b.text(tengah(baris.trim(), w).trimStart());
        b.raw(ESC, 0x61, 0x00);
    }
    b.text('');
    b.text('');
    if (p.hasCutter)
        b.raw(GS, 0x56, 0x42, 0x00); // GS V B — potong sebagian
    /*
     * ⛔ **Tiga syarat, semuanya wajib**, dan masing-masing menutup kebocoran yang berbeda:
     * 1. printer punya laci (`hasDrawerKick`) — kemampuan perangkat;
     * 2. dokumennya **struk pembayaran**, bukan tagihan — kalau tidak, laci bisa dibuka tanpa
     *    transaksi apa pun dengan menekan "cetak bill" berulang kali;
     * 3. ada tender yang metode bayarnya memang **membuka laci** (`payment_method.opens_drawer`) —
     *    laci bukan sifat printer, melainkan keputusan metode bayar. QRIS tak boleh membukanya.
     */
    const hasCash = r.tenders.some((t) => t.opensDrawer === true);
    if (p.hasDrawerKick && docType === 'receipt' && hasCash && opts.kickDrawer !== false) {
        b.raw(...DRAWER_KICK);
    }
    return b.bytes();
}
/** Bentuk terbaca-manusia untuk golden file & diagnosa: byte kontrol jadi `<HEX>`. */
export function toGolden(bytes) {
    let out = '';
    for (const c of bytes) {
        if (c === 0x0a)
            out += '\n';
        else if (c < 0x20 || c > 0x7e)
            out += `<${c.toString(16).padStart(2, '0').toUpperCase()}>`;
        else
            out += String.fromCharCode(c);
    }
    return out;
}
/**
 * ⭐⭐⭐ **Pratinjau struk untuk layar** — dan ia **tak merender apa pun**.
 *
 * ### ⛔ Kenapa ini menanggalkan byte, bukan menyusun teks sendiri
 * Layar template struk butuh menampilkan hasilnya sebelum kertas keluar. Menyusunnya di layar
 * berarti **renderer kedua** — dan ia akan menyimpang pada perubahan pertama: pemotongan `..`,
 * alih-aksara ASCII, lebar 32 vs 48 kolom, urutan kepala. Yang melihat selisihnya adalah tamu,
 * bukan pemilik yang tadi menekan "Simpan" sambil menatap pratinjau yang terlihat benar.
 *
 * ⇒ Masukannya keluaran `renderReceipt` yang sama persis dengan yang dikirim ke printer. Pratinjau
 *   otomatis ikut tiap perubahan renderer, tanpa ada yang perlu ingat menyamakannya.
 *
 * ### ⚠️ Yang TIDAK bisa ditampilkan, dan disebut apa adanya
 * Logo raster jadi placeholder `[ LOGO 384×120 ]`, bukan gambar: pratinjau ini teks, dan menggambar
 * ulang rasternya di sini berarti menebak bagaimana printer memetakan titik. Yang dijamin cocok
 * adalah **tata letak & isi teksnya**.
 *
 * @param bytes keluaran {@link renderReceipt}.
 */
export function toPreview(bytes) {
    const out = [];
    let line = '';
    /** `0` kiri · `1` tengah · `2` kanan — perataan terakhir yang diperintahkan printer. */
    let align = 0;
    let width = 48;
    const flush = () => {
        /*
         * ⭐ Perataan diterapkan DI SINI. Kepala struk yang tampil rata kiri di pratinjau membuat
         * pemilik menatanya dengan spasi manual — lalu di kertas ia jadi miring ke kanan.
         */
        const t = line.replace(/\s+$/, '');
        if (align === 1)
            out.push(' '.repeat(Math.max(0, Math.floor((width - t.length) / 2))) + t);
        else if (align === 2)
            out.push(' '.repeat(Math.max(0, width - t.length)) + t);
        else
            out.push(t);
        line = '';
    };
    for (let i = 0; i < bytes.length; i += 1) {
        const c = bytes[i] ?? 0;
        if (c === 0x0a) {
            flush();
            continue;
        }
        if (c === ESC) {
            const cmd = bytes[i + 1];
            /* ESC a n — perataan. Satu-satunya perintah yang MENGUBAH tampilan pratinjau. */
            if (cmd === 0x61) {
                align = bytes[i + 2] ?? 0;
                i += 2;
                continue;
            }
            /* ESC @ (reset) · ESC t n · ESC E n · ESC d n · ESC p m t1 t2 — tak terlihat di kertas. */
            if (cmd === 0x40) {
                align = 0;
                i += 1;
                continue;
            }
            if (cmd === 0x74 || cmd === 0x45 || cmd === 0x64) {
                i += 2;
                continue;
            }
            if (cmd === 0x70) {
                i += 4;
                continue;
            }
            i += 1;
            continue;
        }
        if (c === GS) {
            /* GS v 0 m xL xH yL yH + data — logo. Ditampilkan sebagai penanda, lihat catatan di atas. */
            if (bytes[i + 1] === 0x76 && bytes[i + 2] === 0x30) {
                const perRow = (bytes[i + 4] ?? 0) | ((bytes[i + 5] ?? 0) << 8);
                const rows = (bytes[i + 6] ?? 0) | ((bytes[i + 7] ?? 0) << 8);
                /* ⚠️ Hanya di-flush bila memang ada teks tertunda — logo selalu di baris pertama, dan
                   baris kosong di puncak pratinjau terbaca sebagai kertas yang terbuang. */
                if (line !== '')
                    flush();
                const dulu = align;
                align = 1;
                line = `[ LOGO ${String(perRow * 8)}x${String(rows)} ]`;
                flush();
                align = dulu;
                i += 7 + perRow * rows;
                continue;
            }
            /* GS V m — potong kertas. */
            if (bytes[i + 1] === 0x56) {
                i += 3;
                continue;
            }
            /*
             * `GS ! n` — ukuran huruf. ⛔ WAJIB dilewati BERSAMA argumennya: tanpa ini `n` (mis. 0x11)
             * masuk ke pratinjau sebagai KARAKTER, dan barisnya terhitung satu kolom lebih panjang dari
             * kertas yang sebenarnya. Ketahuan dari RC2 yang mendadak membaca 49 kolom di kertas 80 mm.
             *
             * ⚠️ Pratinjau tak melebarkan hurufnya — yang dobel lebar tetap ditampilkan satu kolom per
             * huruf. Batasnya tetap jujur karena renderer sudah memenggal pada separuh lebar sebelum
             * sampai ke sini.
             */
            if (bytes[i + 1] === 0x21) {
                i += 2;
                continue;
            }
            i += 1;
            continue;
        }
        line += String.fromCharCode(c);
    }
    if (line !== '')
        flush();
    /*
     * ⚠️ Lebar diturunkan dari isinya, bukan diminta sebagai argumen: garis pemisah `---` sudah
     * selebar kertas, jadi ia SATU-SATUNYA sumber yang pasti cocok dengan yang dipakai renderer.
     * Meminta lebar sebagai argumen membuka peluang layar mengirim angka yang berbeda dari profilnya.
     */
    width = Math.max(1, ...out.map((l) => l.length));
    return out
        .map((l) => (l.startsWith(' ') || l === '' ? l : l))
        .join('\n')
        .replace(/[ \t]+$/gm, '');
}
/**
 * Merender X-report / Z-report.
 *
 * ⛔⛔ **TAK ADA tendangan laci di sini**, dan itu bukan kelalaian. `ESC p` lahir dari
 * `payment_method.opens_drawer` pada struk PEMBAYARAN (K27). Rekap sesi bisa dicetak berkali-kali
 * dan tak menerima uang sepeser pun; kalau ia membuka laci, "cetak X-report" jadi tombol buka-laci
 * yang tak butuh izin `drawer:open` maupun alasan.
 *
 * ⚠️ Judulnya menyebut jenisnya dengan huruf besar dan tebal. Dua lembar yang cuma beda di satu
 * baris kecil akan tertukar di meja yang sama — dan yang satu ditandatangani, yang satu tidak.
 */
export function renderShiftReport(r, p) {
    const w = COLUMNS[p.paperWidth];
    const b = new Buf();
    /*
     * ⭐⭐ Dua jenis garis, dan bedanya bukan hiasan (Imron, 2026-08-25 — contoh struk shift):
     * `=` memisahkan BAGIAN BESAR (kepala · judul · penutup), `-` memisahkan blok di dalamnya.
     * Lembar setoran dibaca sambil menghitung uang; satu jenis garis membuat mata kehilangan tempat.
     */
    const tebal = () => b.text('='.repeat(w));
    const garis = () => b.text('-'.repeat(w));
    const baris = (kiri, kanan) => b.text(twoColumns(kiri, kanan, w));
    /*
     * ⚠️ Kolom uang memakai awalan `Rp` DAN lebar tetap, supaya digit ribuan berbaris lurus ke bawah.
     * Angka yang tak berbaris memaksa pembaca menghitung ulang dengan jari — dan yang membacanya
     * sedang memegang uang tunai.
     */
    const LEBAR_UANG = 13;
    const rp = (nilai) => `Rp ${formatRupiah(nilai).padStart(LEBAR_UANG - 3)}`;
    const uang = (kiri, nilai) => baris(kiri, rp(nilai));
    const judul = (t) => { b.align('center'); b.text(truncate(t, w)); b.align('left'); };
    b.raw(ESC, 0x40);
    b.raw(ESC, 0x74, p.codepage);
    /* ── kepala ─────────────────────────────────────────────────────────── */
    tebal();
    b.align('center');
    b.raw(ESC, 0x45, 0x01);
    b.text(truncate(r.outletName, w));
    b.raw(ESC, 0x45, 0x00);
    if (r.outletAddress !== undefined && r.outletAddress.trim() !== '') {
        b.text(truncate(r.outletAddress.trim(), w));
    }
    b.align('left');
    tebal();
    b.raw(ESC, 0x45, 0x01);
    /*
     * ⛔ X wajib mengaku SEMENTARA di JUDULNYA, bukan di catatan kecil. Lembar tanpa itu akan
     * disimpan orang sebagai bukti tutup kasir, lalu angkanya berbeda dari Z yang terbit kemudian —
     * tanpa ada satu pun yang salah.
     */
    judul(r.kind === 'x' ? 'LAPORAN SEMENTARA (X-REPORT)' : 'LAPORAN CLOSING SHIFT');
    b.raw(ESC, 0x45, 0x00);
    tebal();
    baris('No. Shift', truncate(r.shiftNo, Math.max(0, w - 11)));
    baris('Tanggal usaha', r.businessDate);
    baris('Buka', r.openedAt);
    if (r.closedAt !== null)
        baris('Tutup', r.closedAt);
    baris('Kasir', truncate(r.cashierName, Math.max(0, w - 7)));
    baris('Perangkat', truncate(r.deviceCode, Math.max(0, w - 11)));
    garis();
    /* ── ringkasan penjualan ────────────────────────────────────────────── */
    b.text('RINGKASAN PENJUALAN');
    garis();
    baris('Total Transaksi', `${String(r.bills.paid)} Transaksi`);
    if (r.bills.voided > 0)
        baris('Bill Void', String(r.bills.voided));
    /* ⛔ Bill yang masih terbuka DISEBUT, bukan disembunyikan: menyerahkan laci sementara ada bill
       menggantung berarti uangnya belum tentu masuk — dan itu harus terbaca SEBELUM uang dihitung. */
    if (r.bills.open > 0)
        baris('Bill BELUM SELESAI', String(r.bills.open));
    uang('Penjualan Gross', r.sales.gross);
    if (r.sales.discount !== '0.0000')
        uang('Diskon/Voucher', `-${r.sales.discount}`);
    if (r.sales.serviceCharge !== '0.0000')
        uang('Service Charge', r.sales.serviceCharge);
    if (r.sales.tax !== '0.0000')
        uang('Pajak', r.sales.tax);
    garis();
    b.raw(ESC, 0x45, 0x01);
    uang('TOTAL SALES NETT', r.sales.grandTotal);
    b.raw(ESC, 0x45, 0x00);
    b.text('');
    /* ── metode pembayaran ──────────────────────────────────────────────── */
    b.text('RINCIAN METODE PEMBAYARAN:');
    if (r.tenders.length === 0)
        b.text('  (belum ada)');
    for (const t of r.tenders) {
        uang(`- ${truncate(t.methodName, Math.max(0, w - LEBAR_UANG - 8))} x${String(t.count)}`, t.amount);
    }
    b.text('');
    /* ── rekonsiliasi kas ───────────────────────────────────────────────── */
    garis();
    b.text('REKONSILIASI KAS TUNAI');
    garis();
    uang('Modal Awal', r.cash.openingFloat);
    /* ⛔ Yang tak diketahui TIDAK dicetak sebagai nol (Guardrail #8): "Rp 0" di sini membuat rantai
       modal→diharapkan tak menjumlah, dan yang membacanya sedang memegang uangnya. */
    if (r.cash.cashSales !== undefined)
        uang('Sales Cash (Sistem)', r.cash.cashSales);
    garis();
    b.raw(ESC, 0x45, 0x01);
    uang('Total Uang Diharapkan', r.cash.expected);
    b.raw(ESC, 0x45, 0x00);
    if (r.cash.counted !== null) {
        b.text('');
        uang('Fisik Dihitung (Actual)', r.cash.counted);
        /*
         * ⭐⭐ **Ditahan vs disetor** (Imron, 2026-08-25). Yang benar-benar terjadi di laci ada DUA:
         * sebagian ditahan sebagai modal shift berikutnya, sisanya masuk brankas. ⛔ Yang DISETOR
         * dihitung di sini, tak pernah disimpan — kolom turunan yang disimpan kelak berselisih dengan
         * sumbernya, dan yang menemukannya justru orang yang sedang menyelidiki selisih.
         * ⚠️ `undefined` = tak dinyatakan ⇒ barisnya tak dicetak, bukan dicetak nol.
         */
        if (r.cash.retained !== undefined) {
            const setor = (Number(r.cash.counted) - Number(r.cash.retained)).toFixed(4);
            baris('Fisik Dipertahankan', `${rp(r.cash.retained)}`);
            b.text(twoColumns('', '(Modal Next Shift)', w));
            uang('Fisik Disetor (Actual)', setor);
        }
    }
    if (r.cash.variance !== null) {
        /*
         * ⭐ Selisihnya DIBERI NAMA, bukan cuma angka: "0" tanpa kata bisa dibaca sebagai kolom yang
         * belum diisi. `PAS` · `LEBIH` · `KURANG` — dan yang menandatangani lembar ini membacanya
         * sambil menyerahkan uang.
         */
        const n = Number(r.cash.variance);
        const kata = n === 0 ? '(PAS)' : n > 0 ? '(LEBIH)' : '(KURANG)';
        b.raw(ESC, 0x45, 0x01);
        baris('STATUS SELISIH KAS', `${rp(r.cash.variance)} ${kata}`);
        b.raw(ESC, 0x45, 0x00);
        if (r.cash.varianceReasonCode !== null)
            b.text(`  Alasan: ${r.cash.varianceReasonCode}`);
    }
    garis();
    /* ⭐ Refund berdiri SEBELUM blok laci: ia menjelaskan angka `expected` di atasnya, jadi ia
       harus terbaca bersama angka itu — bukan di kaki bersama catatan lain-lain. */
    if (r.refunds !== undefined && r.refunds.count > 0) {
        b.text('PENGEMBALIAN (REFUND)');
        baris(`  ${String(r.refunds.count)} transaksi`, rp(r.refunds.total));
        /* ⚠️ Yang TUNAI disebut terpisah: cuma itu yang keluar dari laci, dan cuma itu yang
           menjelaskan kenapa `expected` lebih kecil dari penjualan tunai. */
        baris('  di antaranya TUNAI', rp(r.refunds.cash));
        garis();
    }
    if (r.drawerOpens.length > 0) {
        b.text('LACI DIBUKA TANPA TRANSAKSI');
        for (const d of r.drawerOpens)
            baris(`  ${d.reasonCode}`, String(d.count));
        garis();
    }
    /*
     * ⭐⭐ **Kolom tanda tangan** (Imron, 2026-08-25). ⛔ Bukan hiasan: lembar setoran kas berpindah
     * tangan, dan yang membuatnya bisa dipertanggungjawabkan adalah DUA tanda tangan di kertas yang
     * sama dengan angkanya. ⚠️ Nama penyelia sengaja KOSONG — sistem tak tahu siapa yang menerima
     * setoran, dan mencetak nama yang ditebak jauh lebih buruk daripada garis kosong.
     */
    if (r.kind === 'z') {
        b.text('');
        b.text(twoColumns('Kasir,', 'Penyelia,', w));
        b.text('');
        b.text('');
        b.text('');
        /*
         * ⭐ Nama penyelia dicetak HANYA bila ia benar-benar membuktikan kehadirannya (step-up PIN).
         * ⛔ Yang tak dinyatakan tetap garis kosong — nama yang ditebak pada lembar serah-terima uang
         * jauh lebih buruk daripada kolom yang jujur kosong.
         */
        const spv = r.witnessName === undefined || r.witnessName.trim() === ''
            ? '(              )'
            : `( ${truncate(r.witnessName, 14)} )`;
        b.text(twoColumns(`( ${truncate(r.cashierName, 14)} )`, spv, w));
    }
    tebal();
    b.align('center');
    b.text(r.printedAt);
    if (r.kind === 'x')
        b.text('*** SESI MASIH BERJALAN ***');
    b.align('left');
    b.text('');
    b.text('');
    if (p.hasCutter)
        b.raw(GS, 0x56, 0x42, 0x00);
    return b.bytes();
}
/**
 * Merender tiket dapur.
 *
 * ### ⭐⭐⭐ Yang membuat tiket ini terbaca dari dua meter: HANYA TIGA HAL
 * Label (meja/antrean) besar di atas · jumlah di DEPAN nama · nama tebal. Dapur membacanya sambil
 * tangan sibuk dan mata bergerak cepat; apa pun selain ketiganya adalah kebisingan.
 *
 * ### ⛔⛔ Ronde SUSULAN wajib berteriak
 * Tiket kedua untuk meja yang sama adalah **tambahan**, bukan pesanan baru. Kalau ia terlihat sama
 * dengan tiket pertama, dapur memasak seluruh isinya lagi — dan yang menemukan cuma tamu yang
 * menerima dua porsi. ⇒ ronde >1 dicetak sebagai baris tebal tersendiri, bukan angka kecil di sudut.
 *
 * ⛔ **Tanpa tendangan laci**, alasan yang sama dengan rekap sesi: `ESC p` milik struk PEMBAYARAN
 * (K27). Tiket dapur bahkan tak dekat dengan uang.
 */
export function renderKitchenTicket(t, p) {
    const w = COLUMNS[p.paperWidth];
    const b = new Buf();
    b.raw(ESC, 0x40);
    b.raw(ESC, 0x74, p.codepage);
    b.align('center');
    b.raw(ESC, 0x45, 0x01);
    b.text(truncate(t.stationName, w));
    /* ⭐ Label DIGANDAKAN tingginya (`GS ! 0x11`) — ini satu-satunya hal yang dibaca dari jauh. */
    b.raw(GS, 0x21, 0x11);
    b.text(truncate(t.label, Math.floor(w / 2)));
    b.raw(GS, 0x21, 0x00);
    b.raw(ESC, 0x45, 0x00);
    b.align('left');
    b.text(twoColumns(t.orderNo, t.printedAt, w));
    if (t.sendRound > 1) {
        /* ⛔⛔ Lihat kepala fungsi: tiket susulan yang terlihat seperti tiket pertama = masakan dobel. */
        b.text('-'.repeat(w));
        b.raw(ESC, 0x45, 0x01);
        b.align('center');
        b.text(`*** TAMBAHAN (ronde ${String(t.sendRound)}) ***`);
        b.align('left');
        b.raw(ESC, 0x45, 0x00);
    }
    b.text('-'.repeat(w));
    for (const l of t.lines) {
        /* ⚠️ Komponen set menu menjorok DUA spasi; kepalanya tidak. Tanpa jorokan, "Nasi Putih" di
           bawah "Set A" terbaca sebagai pesanan terpisah. */
        const indent = l.isChild ? '  ' : '';
        b.raw(ESC, 0x45, 0x01);
        /* ⭐ Jumlah di DEPAN nama — mata dapur mencari angkanya lebih dulu, bukan namanya. */
        b.text(truncate(`${indent}${formatQty(l.qty)} x ${l.name}`, w));
        b.raw(ESC, 0x45, 0x00);
        if (l.detail !== null && l.detail.trim() !== '') {
            /* ⚠️ Modifier & catatan TIDAK ditebalkan: yang tebal harus sedikit, kalau tidak tak ada lagi
               yang menonjol. Tapi ia tetap dicetak penuh — "tanpa sambal" yang terpotong berbahaya.
               ⚠️ Jorokan ditempel SESUDAH dipecah, bukan sebelum: `pecahBaris` memecah per KATA dan
               akan menelan spasi di depan, sehingga catatan jatuh ke kolom nol dan terbaca sebagai
               barang pesanan tersendiri. */
            const sub = `${indent}    `;
            for (const potong of pecahBaris(l.detail.trim(), w - sub.length))
                b.text(`${sub}${potong}`);
        }
    }
    b.text('-'.repeat(w));
    b.text('');
    b.text('');
    if (p.hasCutter)
        b.raw(GS, 0x56, 0x42, 0x00);
    return b.bytes();
}
/**
 * Memecah teks panjang jadi beberapa baris selebar kertas.
 *
 * ⛔ Catatan dapur **tak boleh dipenggal** dengan `..` seperti nama produk di struk: "tanpa sambal,
 * pedas sedikit" yang terpotong jadi "tanpa sambal, pe.." bisa dibaca sebagai instruksi yang lain
 * sama sekali. Yang panjang dilanjutkan ke baris berikutnya, bukan dibuang.
 */
function pecahBaris(s, lebar) {
    const kata = s.split(/\s+/).filter((x) => x !== '');
    const out = [];
    let baris = '';
    for (const k of kata) {
        if (baris === '') {
            baris = k;
            continue;
        }
        if (`${baris} ${k}`.length <= lebar)
            baris = `${baris} ${k}`;
        else {
            out.push(baris);
            baris = k;
        }
    }
    if (baris !== '')
        out.push(baris);
    return out.length === 0 ? [s] : out;
}
/**
 * ⭐⭐⭐ **Merakit tiket dapur DI LAYAR** — untuk tier tablet yang sedang luring.
 *
 * ### ⛔ Kenapa ini ada, padahal server sudah bisa
 * `buildKitchenTicketJob` membacanya dari database, dan itu jalur yang benar selama server
 * terjangkau. Yang tak punya jalan adalah **warung berdapur yang internetnya putus**: tombol Bayar
 * menuntut baris berangkat ke dapur, `sendToKitchen` lewat server, jadi seluruh outlet berhenti
 * jualan — padahal justru merekalah yang internetnya paling sering putus.
 *
 * ⚠️ Yang dirakit di sini **cuma kertasnya**. Baris `kitchen_ticket` tetap lahir di server saat
 * dokumen penjualannya menyusul (`sale-ingest` memanggil `sendToKitchen`) — jadi KDS & laporan
 * tetap melihatnya, dan tak ada dua sumber kebenaran soal "apa yang pernah dimasak".
 *
 * ⛔ Produk yang BELUM DIRUTEKAN tak diam-diam dilewati: ia dikembalikan di `unrouted`, dan
 * pemanggil wajib memutuskan. Menu baru yang lupa disetel tak boleh dianggap tak perlu dimasak —
 * itu K17, dan alasannya sama di sini.
 */
export function assembleLocalKitchenTickets(input) {
    const perStation = new Map();
    const unrouted = [];
    for (const l of input.lines) {
        const r = input.routes.find((x) => x.productCode === l.code);
        if (r === undefined) {
            unrouted.push(l.code);
            continue;
        }
        const st = input.stations.find((x) => x.code === r.stationCode);
        /*
         * ⛔ Stasiun ber-`kind: counter` sengaja TANPA tiket (K17): rokok & botolan dirutekan ke sana
         * supaya barisnya keluar dari `draft` tanpa menerbitkan kertas. Melewatkannya di sini BUKAN
         * "belum dirutekan" — ia tujuan yang eksplisit.
         */
        if (st === undefined || st.kind === 'counter')
            continue;
        const list = perStation.get(st.code) ?? [];
        list.push(l);
        perStation.set(st.code, list);
    }
    const tickets = [];
    for (const [code, list] of perStation) {
        const st = input.stations.find((x) => x.code === code);
        if (st === undefined)
            continue;
        const docLines = [];
        for (const l of list) {
            docLines.push({
                name: l.name,
                /* ⚠️ Dapur membaca "2", bukan "2.000000" — dan "0,5" bila memang setengah porsi.
                   ⛔ Dulu `Math.round` di sini; itu membuat 0,5 kg tercetak 1 kg di tiket dapur. */
                qty: formatQty(String(l.qty)),
                detail: null,
                isHeader: false,
                isChild: false,
            });
            /* ⭐ Modifier jadi baris ANAK yang menjorok — sama seperti di KDS & di tiket server. */
            for (const m of l.modifierLabels) {
                docLines.push({ name: m, qty: '', detail: null, isHeader: false, isChild: true });
            }
        }
        tickets.push({
            station: st.code,
            printer: st.printer,
            doc: {
                stationName: st.name,
                label: input.label,
                orderNo: input.orderNo,
                sendRound: input.sendRound,
                printedAt: input.printedAt,
                lines: docLines,
            },
        });
    }
    return { tickets, unrouted };
}
export function renderStoreReport(r, p) {
    const w = COLUMNS[p.paperWidth];
    const b = new Buf();
    const tebal = () => b.text('='.repeat(w));
    const garis = () => b.text('-'.repeat(w));
    const baris = (kiri, kanan) => b.text(twoColumns(kiri, kanan, w));
    const LEBAR_UANG = 13;
    const rp = (nilai) => `Rp ${formatRupiah(nilai).padStart(LEBAR_UANG - 3)}`;
    const uang = (kiri, nilai) => baris(kiri, rp(nilai));
    b.raw(ESC, 0x40);
    b.raw(ESC, 0x74, p.codepage);
    tebal();
    b.align('center');
    b.raw(ESC, 0x45, 0x01);
    b.text(truncate(r.outletName, w));
    b.raw(ESC, 0x45, 0x00);
    if (r.outletAddress !== undefined && r.outletAddress.trim() !== '') {
        b.text(truncate(r.outletAddress.trim(), w));
    }
    b.align('left');
    tebal();
    b.raw(ESC, 0x45, 0x01);
    b.align('center').text('Z-REPORT (CLOSING STORE)').align('left');
    b.raw(ESC, 0x45, 0x00);
    tebal();
    baris('No. Report', truncate(r.reportNo, Math.max(0, w - 12)));
    baris('Tanggal usaha', r.businessDate);
    baris('Ditutup', r.closedAt);
    baris('Store ID', truncate(r.outletCode, Math.max(0, w - 10)));
    baris('Oleh', truncate(r.closedByName, Math.max(0, w - 6)));
    /*
     * ⛔⛔ Persetujuan DISEBUT, bukan disembunyikan. Ia terisi HANYA bila hari ditutup dalam keadaan
     * kotor (masih ada sesi/pesanan terbuka, atau selisih kas) — dan justru itulah lembar yang paling
     * perlu dibaca ulang esok hari.
     */
    if (r.approvedByCode !== null)
        baris('Disetujui', truncate(r.approvedByCode, Math.max(0, w - 11)));
    garis();
    b.text('TOTAL PERFORMA HARIAN');
    garis();
    baris('Total Shift Ditutup', `${String(r.shiftCount)} Shift`);
    baris('Total Bills', `${String(r.billCount)} Transaksi`);
    /* ⛔ Pesanan yang MASIH TERBUKA saat toko ditutup wajib terbaca di lembar ini: uangnya belum
       tentu masuk, dan angka omzet di atasnya tak memuatnya. */
    if (r.openOrderCount > 0)
        baris('⚠ Pesanan BELUM SELESAI', String(r.openOrderCount));
    uang('Gross Sales', r.sales.gross);
    if (r.sales.discount !== '0.0000')
        uang('Total Diskon', `-${r.sales.discount}`);
    /* ⚠️ Refund berdiri SENDIRI, tak dikurangkan diam-diam dari bruto: "berapa yang dikembalikan
       hari ini" adalah pertanyaan tersendiri, dan yang menjawabnya angka yang bisa dilihat. */
    if (r.sales.refund !== '0.0000')
        uang('Total Refund', `-${r.sales.refund}`);
    if (r.sales.serviceCharge !== '0.0000')
        uang('Service Charge', r.sales.serviceCharge);
    if (r.sales.tax !== '0.0000')
        uang('Pajak', r.sales.tax);
    garis();
    b.raw(ESC, 0x45, 0x01);
    uang('GRAND TOTAL OMSET', r.sales.grandTotal);
    b.raw(ESC, 0x45, 0x00);
    b.text('');
    b.text('TOTAL PENDAPATAN PER METODE:');
    if (r.tenders.length === 0)
        b.text('  (tak ada penjualan)');
    for (const t of r.tenders) {
        uang(`- ${truncate(t.methodName, Math.max(0, w - LEBAR_UANG - 8))} x${String(t.count)}`, t.amount);
    }
    b.text('');
    /*
     * ⭐⭐ **Selisih PER SESI, bukan cuma totalnya.** Total nol bisa berarti dua sesi yang saling
     * meniadakan (+50rb dan −50rb) — dan itu keadaan yang harus terlihat, bukan yang paling
     * tersembunyi. Menampilkan totalnya saja membuat malam seperti itu terbaca "beres".
     */
    b.text('SUMMARY VARIANCE / SELISIH:');
    if (r.shifts.length === 0)
        b.text('  (tak ada sesi kas)');
    for (const s of r.shifts) {
        const nama = s.cashierName === null ? s.shiftNo : `${s.shiftNo} (${s.cashierName})`;
        uang(`- ${truncate(nama, Math.max(0, w - LEBAR_UANG - 4))}`, s.variance ?? '0.0000');
    }
    garis();
    const n = Number(r.totalVariance);
    b.raw(ESC, 0x45, 0x01);
    baris('TOTAL OVER/SHORT HARIAN', `${rp(r.totalVariance)} ${n === 0 ? '(PAS)' : n > 0 ? '(LEBIH)' : '(KURANG)'}`);
    b.raw(ESC, 0x45, 0x00);
    tebal();
    b.align('center');
    b.raw(ESC, 0x45, 0x01);
    b.text('*** END OF DAY ***');
    b.raw(ESC, 0x45, 0x00);
    b.align('left');
    tebal();
    b.text('');
    b.text('');
    if (p.hasCutter)
        b.raw(GS, 0x56, 0x42, 0x00);
    return b.bytes();
}
//# sourceMappingURL=escpos.js.map